import { isNativeAppWebView } from 'utils/webview';

/** Opts a field out of browser autofill entirely. */
export const NO_AUTOFILL = 'off';

/**
 * Build the `autocomplete` value for a field that belongs to one person's section of a form.
 *
 * Browser autofill groups fields by **type**, not by person: with several passengers in one
 * form, picking a suggestion for passenger 1 fills every other passenger's name with the same
 * identity. The `section-*` token is the mechanism the HTML spec provides for exactly this —
 * it marks the field as belonging to a named section, so the browser fills only the section
 * the user is actually in.
 *
 * Inside the native app WebView autofill is switched off instead of sectioned. Section
 * detection is a browser heuristic, and getting it wrong on the passengers step means a
 * traveller boards with someone else's name; the app also has its own remembered-passenger
 * flows, so the browser's suggestion adds little there.
 *
 * @param {string} section - Section name, e.g. `passenger-1` or `purchaser`.
 * @param {string} token - Standard autocomplete token, e.g. `given-name`, `email`, `tel-national`.
 * @returns {string} The value for the input's `autocomplete` attribute.
 */
export const sectionAutoComplete = (section, token) =>
  isNativeAppWebView() ? NO_AUTOFILL : `section-${section} ${token}`;

/**
 * Build the `autocomplete` value for a field of the Nth passenger.
 *
 * @param {number} index - Zero-based passenger index, as given by the redux-form FieldArray.
 * @param {string} token - Standard autocomplete token, e.g. `given-name`.
 * @returns {string} The value for the input's `autocomplete` attribute.
 */
export const passengerAutoComplete = (index, token) =>
  sectionAutoComplete(`passenger-${index + 1}`, token);

/**
 * Build the `autocomplete` value for a field of the purchaser form.
 *
 * The purchaser is its own section: the buyer is frequently not passenger 1, and without the
 * token the browser would happily reuse one identity for both.
 *
 * @param {string} token - Standard autocomplete token, e.g. `email`.
 * @returns {string} The value for the input's `autocomplete` attribute.
 */
export const purchaserAutoComplete = (token) => sectionAutoComplete('purchaser', token);
